<template>
  <div
    :class="`${slideData.customClass} ${slideData.theme ? `stage-slide--${slideData.theme}` : ''}`"
    :style="cssBackground"
    class="stage-slide"
  >
    <CdnImage
      v-if="slideData.background?.image"
      :alt="slideData.background.image.alt || ''"
      :src="slideData.background.image.srcSmall"
      :src-sm="slideData.background.image.srcMedium"
      :src-md="slideData.background.image.srcLarge"
      :sizes="slideData.background.image.sizes || ''"
      :do-not-convert="!slideData.background.image.sizes"
      :lazy="lazyLoading"
      :preload="!lazyLoading"
      class="stage-slide__background"
    />
    <div class="stage-slide__container">
      <div class="stage-slide__content">
        <div
          :class="cssTheme"
          class="stage-slide__information"
        >
          <div class="stage-slide__information-container">
            <div class="stage-slide__headline-wrapper">
              <CdnImage
                v-if="brand"
                class="stage-slide__headline-brand"
                :alt="brand.alt || brand.name"
                :src="brand.src || brand.logo"
                :style="brand.style"
                :sizes="brand.sizes"
                render-without-figure
                :lazy="lazyLoading"
                :preload="!lazyLoading"
              />
              <Paragraph
                :text="headline"
                :theme="slideData.theme"
                class="stage-slide__headline"
              />
            </div>
            <p
              v-if="subline"
              class="text stage-slide__subline"
            >
              <span v-html="subline"></span>
              <LinkButton
                v-if="slideData.modal?.opener?.includes('footnote')"
                tag="button"
                class="btn btn--footnote"
                type="button"
                aria-label="Fußnote"
                @click="openModalHandler"
              >
                *
              </LinkButton>
            </p>
            <div
              v-if="!ctaButton?.hideButton"
              class="stage-slide__button-container stage-slide__button-container--desktop"
            >
              <StageLink
                v-if="ctaButton.url"
                class="stage-slide__stage-link"
                :class="btnTheme"
                :is-external="ctaButton.isExternal"
                :url="ctaButton.url"
                :webtrack="dataTealiumRel"
              >
                {{ ctaButton.buttonText }}
              </StageLink>
              <StageLink
                v-if="slideData.secondaryCtaButton"
                class="stage-slide__stage-link"
                :class="btnTheme"
                :is-external="slideData.secondaryCtaButton.isExternal || false"
                :url="slideData.secondaryCtaButton.url"
                :webtrack="dataTealiumRel"
              >
                {{ slideData.secondaryCtaButton.buttonText }}
              </StageLink>
              <LinkButton
                v-if="slideData.modal?.opener?.includes('button')"
                :class="btnTheme"
                tag="button"
                class="stage-slide__stage-link"
                type="button"
                @click="openModalHandler"
              >
                {{ slideData.modal.openButtonLabel || 'Details' }}
              </LinkButton>
            </div>
          </div>
        </div>
        <div
          v-if="visual"
          :class="hasVisualAlignment"
          class="stage-slide__visual-container"
        >
          <CdnImage
            v-if="visual.srcSmall"
            :alt="visual.alt || ''"
            :src="visual.srcSmall"
            :src-md="visual.srcLarge"
            :src-sm="visual.srcMedium"
            :image-style="visual.style"
            :sizes="visual.sizes"
            class="stage-slide__visual"
            :lazy="lazyLoading"
            :preload="!lazyLoading"
          />
          <PromotionBadge
            :badge="slideData.badge"
            :offer="offer"
            :modal-texts="slideData.badgeModal"
          >
            <LinkButton
              v-if="slideData.modal?.opener?.includes('badge')"
              class="btn btn--footnote"
              tag="button"
              type="button"
              aria-label="Fußnote"
              @click="openModalHandler"
            >
              *
            </LinkButton>
          </PromotionBadge>
          <PricesContainer
            v-if="slideData.price && offer?.prices"
            class="stage-slide__prices"
            variant="medium"
            is-price-bold
            :prices-data="offer.prices"
            :pre-text-is-price="slideData.price.preTextIsPrice"
            :pre-text-was-price="slideData.price.preTextWasPrice"
            :style="slideData.price.styles"
            :theme="slideData.theme"
          />
          <Prices
            v-else-if="slideData.price?.default"
            class="stage-slide__prices"
            :is-price="slideData.price.default"
            :was-price="slideData.price.was"
            is-price-bold
            :pre-text-is-price="slideData.price.preTextIsPrice"
            :pre-text-was-price="slideData.price.preTextWasPrice"
            :theme="slideData.theme"
            :style="slideData.price.styles"
          />
        </div>
      </div>
      <div
        v-if="!ctaButton.hideButton"
        class="stage-slide__button-container stage-slide__button-container--mobile"
      >
        <StageLink
          v-if="ctaButton.url"
          class="stage-slide__stage-link"
          :class="btnTheme"
          :is-external="ctaButton.isExternal"
          :url="ctaButton.url"
          :webtrack="dataTealiumRel"
        >
          {{ ctaButton.buttonText }}
        </StageLink>
        <StageLink
          v-if="slideData.secondaryCtaButton"
          class="stage-slide__stage-link"
          :class="btnTheme"
          :is-external="slideData.secondaryCtaButton.isExternal"
          :url="slideData.secondaryCtaButton.url"
          :webtrack="dataTealiumRel"
        >
          {{ slideData.secondaryCtaButton.buttonText }}
        </StageLink>
        <LinkButton
          v-if="slideData.modal?.opener?.includes('button')"
          class="stage-slide__stage-link"
          :class="btnTheme"
          type="button"
          tag="button"
          @click="openModalHandler"
        >
          {{ slideData.modal.openButtonLabel || 'Details' }}
        </LinkButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type PropType, computed, defineAsyncComponent } from 'vue';
import { getPrice } from '@i22/rocket';
import CdnImage from '@/components/cdn-image.vue';
import StageLink from '@/components/stage-link.vue';
import { Paragraph, LinkButton, Prices } from '@i22-td-smarthome/component-library';
import { openModal } from '@/composables/modal';
import { getMainImageFromOffer } from '@/lib/goliath-media';
import type { PromotionOffer } from '@/lib/offers-by-group';
import type { ListOffer } from '@/lib/goliath/offers';
import type { CTA, SlideVisual, StageSliderData } from '@/lib/promotions-type';
import { addLogo, getBrand } from '~/lib/brand';
import PricesContainer from '~/components/product-detail/prices-container.vue';
import PromotionBadge from '~/components/promotion-badge.vue';
import type { ModalButton } from '~/composables/modal-types';

const props = defineProps({
  slideData: {
    type: Object as PropType<PromotionOffer<ListOffer, StageSliderData>>,
    default: () => {},
    required: true,
  },
  lazyLoading: { type: Boolean },
});

const cssBackground = computed(() => {
  const styles = props.slideData.style || {};

  if (props.slideData.background?.color) {
    styles.background = props.slideData.background?.color;
  }
  return styles;
});

const btnTheme = computed(() => {
  if (props.slideData.ctaButton?.theme) {
    return `stage-slide__button stage-slide__button--${props.slideData.ctaButton.theme} ${
      props.slideData.ctaButton.theme === 'light' ? 'btn--transparent-light' : 'btn--transparent'
    }`;
  }
  if (props.slideData.theme === 'light')
    return 'stage-slide__button stage-slide__button--light btn--transparent-light';
  return 'stage-slide__button stage-slide__button--dark btn--transparent';
});

const cssTheme = computed(() => {
  if (props.slideData.theme === 'light') return 'stage-slide__information--light';
  return null;
});

const offer = computed<ListOffer | undefined>(() => props.slideData.offer);

const defaultPrice = computed(() => {
  if (!offer.value?.prices) return undefined;
  const price = getPrice(offer.value.prices || [], 'DEFAULT', 'ONETIME');
  return price?.amount;
});

const wasPrice = computed(() => {
  if (!offer.value?.prices) return undefined;
  const price = getPrice(offer.value.prices || [], 'WAS', 'ONETIME');
  return price?.amount;
});

const headline = computed(() =>
  props.slideData.headline ? props.slideData.headline.text : offer.value?.product?.name
);

const brand = computed(() => {
  if (props.slideData.headline?.brand?.src) {
    return props.slideData.headline?.brand;
  }
  if (props.slideData.headline?.brand?.slug) {
    const brandData = addLogo({
      slug: props.slideData.headline?.brand?.slug,
      name: props.slideData.headline?.brand?.alt,
    });
    if (brandData) {
      return {
        ...brandData,
        ...props.slideData.headline?.brand,
        style: `aspect-ratio: ${brandData.aspectRatio};${props.slideData.headline?.brand?.style}`,
      };
    }
  }
  if (offer.value) {
    const brandData = getBrand(offer.value);
    if (brandData) {
      return {
        ...brandData,
        ...props.slideData.headline?.brand,
        style: `aspect-ratio: ${brandData.aspectRatio};${props.slideData.headline?.brand?.style}`,
      };
    }
  }

  return undefined;
});

const headingTag = computed(() => {
  if (!props.slideData.headline?.tag) return 'h2';
  return props.slideData.headline.tag;
});
const subline = computed(() => props.slideData.subline);

const slug = computed(() => offer.value?.product?.slug);

const ctaButton = computed<CTA & StageSliderData['ctaButton']>(() => ({
  buttonText: 'Zum Angebot',
  url: slug.value ? `/geraete/${slug.value}` : undefined,
  ...props.slideData.ctaButton,
}));

// srcMedium and srcLarge is possible as well
const visual = computed<SlideVisual & StageSliderData['visual']>(() => {
  const defaultVisual: SlideVisual = {
    srcSmall: undefined,
    verticalAlignment: 'center',
  };

  if (offer.value && !props.slideData.visual?.srcSmall) {
    const goliathImage = getMainImageFromOffer(offer.value, 0);
    if (goliathImage) {
      defaultVisual.srcSmall = goliathImage.small;
      defaultVisual.srcMedium = goliathImage.medium;
      defaultVisual.srcLarge = goliathImage.large;
    }
  }

  return {
    ...defaultVisual,
    ...props.slideData.visual,
  };
});

const hasVisualAlignment = computed(() => {
  if (!visual.value.verticalAlignment) return 'stage-slide__visual-container--vertical-center';
  return `stage-slide__visual-container--vertical-${visual.value.verticalAlignment}`;
});

const dataTealiumRel = computed(() => {
  if (!ctaButton.value.buttonText) return '';
  return `content.button.${ctaButton.value.buttonText}`;
});

const openModalHandler = () => {
  const buttons: (ModalButton | Record<string, any>)[] = [
    {
      name: 'Schließen',
      tag: 'button',
      transparent: true,
    },
  ];
  const modalBtn = props.slideData.modal?.button;
  if (modalBtn) {
    buttons.push({
      name: modalBtn.label,
      tag: modalBtn.link?.startsWith('http') ? 'a' : 'nuxt-link',
      to: modalBtn.link,
      transparent: true,
    });
  }
  openModal({
    component: {
      is: defineAsyncComponent(() => import('~/components/stage-slide-dialog.vue')),
      props: {
        slideData: props.slideData,
      },
    },
    className: `stage-slide__modal stage-slide__modal--${props.slideData.modal.id}`,
    buttons,
  });
};

defineExpose({
  cssBackground,
  btnTheme,
  cssTheme,
  hasVisualAlignment,
  defaultPrice,
  wasPrice,
  headline,
  headingTag,
  subline,
  ctaButton,
  visual,
  slug,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.stage-slide {
  position: relative;
  display: grid;
  text-align: center;
  min-height: 566px;
  height: 100%;
  width: 100%;

  @include mq($mq-sm) {
    justify-content: flex-start;
    text-align: left;
    height: 420px;
    min-height: initial;
  }
  @include mq($mq-md) {
    height: 520px;
    justify-content: center;
  }

  &__badge {
    position: absolute;
  }

  &__container,
  &__background {
    grid-row: 1;
    grid-column: 1;
  }

  &__background {
    position: relative;
    display: block;
    width: 100vw;
    height: 100%;
    pointer-events: none;

    :deep(img) {
      object-fit: cover;
      display: block;
      width: 100%;
      height: 100%;

      object-position: var(--background-position-xs, 50% 50%);

      @include mq($mq-sm) {
        object-position: var(--background-position-sm, 50% 50%);
      }
      @include mq($mq-md) {
        object-position: var(--background-position-md, 50% 50%);
      }
    }
  }

  &__container {
    width: 100%;
    height: 94%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: 320px - $grid-base * 2;
    padding: $grid-base * 2 0 $grid-base * 3 0;
    @include mq(375px) {
      max-width: 400px;
      height: 100%;
    }
    @include mq($mq-sm) {
      flex-direction: row;
      justify-content: initial;
      max-width: 80%;
      max-height: 420px;
    }
    @include mq($mq-md) {
      width: 100%;
      max-width: 90%;
      padding: 0;
      max-height: 520px;
    }
    @include mq(1200px) {
      max-width: 1000px;
    }
  }

  &__content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: var(--visual-justify-xs, flex-start);

    @include mq($mq-sm) {
      flex-direction: row;
      justify-content: var(--visual-justify-sm, flex-start);
    }
  }

  &__button-container {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding-bottom: $grid-base;
    text-align: center;
    justify-content: center;

    @include mq($mq-sm) {
      padding-top: 2 * $grid-base;
      text-align: left;
      justify-content: flex-start;
    }

    &--mobile {
      @include mq($mq-sm) {
        display: none;
      }
    }

    &--desktop {
      @include mq(false, $mq-sm - 1) {
        display: none;
      }
    }
  }
  &__stage-link {
    padding: $grid-base;
    display: inline-block;
    height: auto;
    font-weight: 700;
    line-height: 1;
  }

  &__button {
    position: relative;
  }

  &__headline {
    font-weight: 800;
    text-transform: uppercase;
    margin-bottom: $grid-base;
    text-align: center;
    @include font-size(27px, 30px);

    @include mq($mq-sm) {
      text-align: left;
      @include font-size(36px, 38px);
    }
    @include mq($mq-md) {
      @include font-size(48px, 54px);
    }

    &-brand {
      height: 40px;
      display: inline-block;
    }
  }

  &__information {
    display: flex;
    align-items: center;
    z-index: 5;
    position: relative;
    color: color(grey, 12000);
    padding: 0 $grid-base;

    &--light {
      color: color(white);
    }

    @include mq($mq-sm) {
      width: calc(100% - var(--visual-width, 60%));
    }

    @include mq($mq-md) {
      padding-right: $grid-base * 2;
    }

    &-container {
      width: 100%;
    }
  }

  &__visual-container {
    position: relative;

    &--vertical-center {
      text-align: center;
    }

    @include mq($mq-sm) {
      display: flex;
      width: var(--visual-width, 40%);
      flex-direction: row-reverse;

      &--vertical-center {
        align-items: center;
      }
      &--vertical-top {
        align-items: flex-start;
      }
      &--vertical-bottom {
        align-items: flex-end;
      }
    }
  }

  &__visual {
    flex: 0 0 100%;

    :deep(img) {
      display: block;
      width: 100%;
      object-fit: contain;
      object-position: center;

      @include mq(false, $mq-sm - 1) {
        height: 200px;
      }

      @include mq($mq-sm) {
        height: 100%;
      }
    }
  }

  &__subline {
    @include font-size(18px, 22px);
    margin-bottom: 12px;

    @include mq($mq-sm) {
      @include font-size(21px, 24px);
      margin-bottom: 32px;
    }
    @include mq($mq-md) {
      @include font-size(24px, 28px);
      margin-bottom: 42px;
    }
  }

  &__modal {
    &-image {
      width: 270px;
      margin-bottom: 2 * $grid-base;
    }

    &-subheading {
      font-weight: 800;
      @include font-size(24px, 29px);
      text-align: center;
    }

    &-heading {
      color: var(--color-primary);
      margin-bottom: $grid-base * 2;
    }
  }

  &__prices {
    display: inline-block;
    margin-top: $grid-base * 2;

    @include mq($mq-sm) {
      margin-top: 50%;

      :deep(.prices__main) {
        flex-direction: column;
        grid-gap: 0;
      }
      :deep(.price) {
        flex-direction: row;
        flex-wrap: nowrap;
      }
    }
  }

  &--light {
    background-color: color(black);
  }

  &.fairphone {
    @include mq($mq-sm) {
      --visual-width: 41%;
    }
    @include mq($mq-md) {
      --visual-width: 56%;
    }

    .stage-slide__content {
      grid-gap: $grid-base * 2;
      margin-bottom: $grid-base;
    }
  }
}

.stage--samsung-cashback-aktion {
  .stage-slide__headline {
    @include mq($mq-sm) {
      max-width: 270px;
    }
    @include mq($mq-md) {
      max-width: none;
    }
  }

  .stage-slide__subline {
    max-width: 340px;

    @include mq($mq-sm) {
      max-width: 270px;
    }
    @include mq($mq-md) {
      max-width: 340px;
    }
  }

  .stage-slide__background {
    position: relative;
    :deep(img) {
      position: absolute;
      object-fit: contain;
      bottom: -15%;
      left: 50%;
      transform: translateX(-51%);
      width: 90vw;
      max-width: 370px;

      @include mq($mq-sm) {
        object-fit: cover;
        max-width: none;
        bottom: auto;
        width: 600px;
        left: 50%;
        transform: translateX(-16%);
      }
      @include mq($mq-md) {
        width: 900px;
        transform: translateX(-24%);
      }
    }
  }

  :deep(.badge__content) {
    padding: 0 0.6rem;
  }

  .stage-slide__badge {
    position: absolute;
    background-color: black;
    top: 0;
    left: 10%;

    @include mq($mq-sm) {
      top: 14%;
      left: -40%;
    }
    @include mq($mq-md) {
      top: 16%;
      left: -40%;
    }
  }

  .stage-slide__badge-text {
    font-weight: 600;
    font-size: 18px;
    line-height: 21px;

    @include mq($mq-md) {
      font-size: 24px;
      line-height: 28px;
    }

    :deep(.stage-slide__badge-text--big) {
      font-size: 25px;

      @include mq($mq-md) {
        font-size: 36px;
      }
    }
  }
}
</style>
